import { CalendarClock, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import WorkerPremiumCard, { WorkerPremiumCardHeader } from './WorkerPremiumCard';

export default function WorkerAvailableShiftsCard({
  loading,
  error,
  onRetry,
  shifts,
  applyingShiftId,
  formatDate,
  formatDateTime,
  onApply,
}) {
  return (
    <WorkerPremiumCard accent="emerald" data-testid="worker-available-shifts-card">
      <WorkerPremiumCardHeader
        icon={CalendarClock}
        title="Available Shifts"
        description="Open shifts that match your role. Tap I'm interested — admin confirms the final assignment."
      />
      <div>
        {loading ? (
          <div role="status" aria-label="Loading available shifts" className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
          </div>
        ) : error ? (
          <div role="alert" className="space-y-3 text-sm text-slate-700">
            <p>{error}</p>
            <Button variant="outline" className="min-h-11 rounded-xl" onClick={onRetry}>Try again</Button>
          </div>
        ) : shifts.length === 0 ? (
          <p className="text-sm text-slate-600">No open shifts match your profile right now.</p>
        ) : (
          <div className="space-y-3">
            {shifts.map((item) => {
              const shift = item.shift || {};
              const isApplied = item.application_status === 'applied';
              const timeStart = formatDateTime(shift.start_at).split(', ').pop();
              const timeEnd = formatDateTime(shift.end_at).split(', ').pop();
              return (
                <div
                  key={shift.id}
                  className="rounded-xl border border-slate-200/80 bg-gradient-to-br from-white to-slate-50/50 p-3 shadow-sm sm:p-4"
                  data-testid={`available-shift-${shift.id}`}
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-slate-900">{shift.role_required || 'Role pending'}</p>
                        <Badge className={isApplied ? 'bg-blue-100 text-blue-800' : 'bg-emerald-100 text-emerald-800'}>
                          {isApplied ? 'Applied' : 'Available'}
                        </Badge>
                      </div>
                      <p className="mt-1 text-sm text-slate-700">{formatDate(shift.start_at)}</p>
                      <p className="mt-0.5 text-sm text-slate-600">{timeStart} – {timeEnd}</p>
                      <p className="mt-1 text-sm text-slate-600">{shift.location_text || 'Location pending'}</p>
                      {shift.care_location ? (
                        <p className="mt-1 text-xs text-slate-500">
                          {shift.care_location.name}
                          {shift.care_location.city ? `, ${shift.care_location.city}` : ''}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:min-w-[9.5rem]">
                      {!isApplied ? (
                        <Button
                          type="button"
                          className="min-h-11 w-full rounded-xl bg-emerald-600 text-white shadow-sm hover:bg-emerald-700 touch-manipulation"
                          onClick={() => onApply(shift.id)}
                          disabled={applyingShiftId === shift.id}
                          data-testid={`apply-shift-${shift.id}`}
                        >
                          {applyingShiftId === shift.id ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : null}
                          I&apos;m interested
                        </Button>
                      ) : (
                        <p className="text-center text-xs text-slate-600 sm:text-right">
                          Awaiting admin confirmation
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </WorkerPremiumCard>
  );
}